import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";

type ThemeState = {
	theme: Theme;
	setTheme: (theme: Theme) => void;
};

// Persisted under "mos.theme"; public/theme-init.js reads the same key before first paint.
export const useThemeStore = create<ThemeState>()(
	persist((set) => ({ theme: "system", setTheme: (theme) => set({ theme }) }), {
		name: "mos.theme",
	}),
);

const darkQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

export function resolveTheme(theme: Theme): "light" | "dark" {
	if (theme === "system") return darkQuery().matches ? "dark" : "light";
	return theme;
}

export function applyTheme(theme: Theme): void {
	const resolved = resolveTheme(theme);
	const root = document.documentElement;
	root.classList.toggle("dark", resolved === "dark");
	document
		.querySelector('meta[name="theme-color"]')
		?.setAttribute("content", resolved === "dark" ? "#16161d" : "#ffffff");
}

/** Keeps <html class="dark"> in sync with the store and the OS setting. Returns an unsubscribe. */
export function bindTheme(): () => void {
	applyTheme(useThemeStore.getState().theme);
	const unsub = useThemeStore.subscribe((s) => applyTheme(s.theme));
	const mq = darkQuery();
	const onChange = () => {
		if (useThemeStore.getState().theme === "system") applyTheme("system");
	};
	mq.addEventListener("change", onChange);
	return () => {
		unsub();
		mq.removeEventListener("change", onChange);
	};
}
